import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { LockClosedIcon, CheckIcon } from '@heroicons/react/24/outline';
import ChartHeader from '../common/ChartHeader';
import GlassyIcon from '../common/GlassyIcon';
import GlassyBadge from '../common/GlassyBadge';

import { OptimizedImage } from '../../utils/imageManager';

// ===================================================================================
//  UI Component for a single Milestone Badge
// ===================================================================================

const MilestoneBadge = ({ icon, imageKey, title, progress, achievementText, isCompleted, isLocked }) => (
  <div
    className={`
      relative flex h-full flex-row items-center overflow-hidden rounded-2xl
      border p-3 text-left transition-all duration-300
      group md:flex-col md:justify-center md:p-4 md:text-center
      ${
        isCompleted
          ? 'border-green-200 bg-gradient-to-br from-green-50 to-emerald-100 text-green-800 dark:border-green-700 dark:from-green-900/50 dark:to-emerald-900/50 dark:text-green-200'
          : isLocked
          ? 'border-border bg-background-secondary/30 text-content-secondary backdrop-blur-sm'
          : 'border-border bg-background-secondary/70 text-content-primary backdrop-blur-sm'
      }
    `}
  >
    {/* Glassmorphic overlay for the default state */}
    {!isCompleted && !isLocked && <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-40" />}

    {/* Animated background elements for completed state */}
    {isCompleted && (
      <div className="absolute inset-0 opacity-10 dark:opacity-20">
        <div className="absolute right-0 top-0 h-16 w-16 -translate-y-8 translate-x-8 rounded-full bg-green-300 dark:bg-green-500" />
        <div className="absolute bottom-0 left-0 h-12 w-12 -translate-x-6 translate-y-6 rounded-full bg-emerald-300 dark:bg-emerald-500" />
      </div>
    )}

    {/* Icon */}
    <div
      className={`
      relative z-10 transition-all duration-300
      mr-4 md:mb-3 md:mr-0
      ${isLocked ? 'grayscale opacity-60' : ''}
    `}
    >
      {imageKey ? (
        <OptimizedImage 
          imageKey={imageKey} 
          className="w-12 h-12 md:w-16 md:h-16 object-contain" 
          fallback={<span className="text-3xl md:text-4xl">{icon}</span>}
        />
      ) : (
        <span className="text-3xl md:text-4xl">{icon}</span>
      )}
    </div>

    {/* Content section */}
    <div className="relative z-10 flex flex-grow flex-col justify-center">
      <h4
        className={`
        text-sm font-bold leading-tight
        ${isCompleted ? 'text-green-800 dark:text-green-200' : isLocked ? 'text-content-secondary' : 'text-content-primary'}
      `}
      >
        {title}
      </h4>

      {/* Achievement text */}
      {achievementText && (
        <GlassyBadge
          color={isCompleted ? 'emerald' : isLocked ? 'neutral' : 'orange'}
          size="sm"
          variant={isCompleted ? 'default' : isLocked ? 'outline' : 'default'}
          className="mt-1"
        >
          {achievementText}
        </GlassyBadge>
      )}

      {/* Progress text */}
      {progress && (
        <div className="relative z-10 mt-2">
          <p
            className={`
            rounded-lg px-2 py-1 text-xs font-bold
            ${
              isCompleted
                ? 'bg-green-200/50 text-green-700 dark:bg-green-500/20 dark:text-green-200'
                : isLocked
                ? 'bg-background-primary/30 text-content-secondary'
                : 'bg-background-primary/70 text-content-secondary'
            }
          `}
          >
            {progress}
          </p>
        </div>
      )}
    </div>

    {/* Completion indicator */}
    {isCompleted && (
      <div className="absolute right-2 bottom-2 md:top-1 md:bottom-auto z-20">
        <GlassyIcon icon={CheckIcon} size="xs" color="emerald" className="md:!w-5 md:!h-5" />
      </div>
    )}

    {/* Lock indicator */}
    {isLocked && (
      <div className="absolute right-2 bottom-2 md:top-1 md:bottom-auto z-20">
        <GlassyIcon icon={LockClosedIcon} size="xs" color="neutral" className="md:!w-5 md:!h-5" />
      </div>
    )}
  </div>
);

MilestoneBadge.propTypes = {
  icon: PropTypes.string,
  imageKey: PropTypes.string,
  title: PropTypes.string.isRequired,
  progress: PropTypes.string,
  achievementText: PropTypes.string,
  isCompleted: PropTypes.bool,
  isLocked: PropTypes.bool,
};

// ===================================================================================
//  Helper Functions to Calculate Milestone State
// ===================================================================================

/**
 * Calculates the state of a standard, progressive milestone (e.g., total items learned).
 */
const getMilestoneState = (currentValue, tiers, icon, name, verb) => {
  const firstTier = tiers[0];

  // Case: User hasn't reached the first tier yet
  if (currentValue < firstTier) {
    return {
      icon,
      title: name,
      progress: `${currentValue.toLocaleString()}/${firstTier.toLocaleString()}`,
      achievementText: `${verb} ${firstTier.toLocaleString()}`,
      isCompleted: false,
      isLocked: true,
    };
  }

  const lastAchievedTier = Math.max(...tiers.filter((tier) => currentValue >= tier), 0);
  const nextTier = tiers.find((tier) => currentValue < tier);
  const isCompleted = !nextTier;

  // Case: User is in progress or has completed all tiers
  return {
    icon,
    title: name,
    progress: isCompleted ? 'All tiers complete!' : `${currentValue.toLocaleString()}/${nextTier.toLocaleString()}`,
    achievementText: `Achieved ${lastAchievedTier.toLocaleString()}`,
    isCompleted,
    isLocked: false,
  };
};

/**
 * Calculates the state of a specific level mastery milestone (e.g., Master N5).
 */
const getLevelMasteryState = (level, sentencesStats, totalCounts) => {
  const levelKey = String(level);
  const name = `Master N${level}`;
  const icon = '🏅';

  const totalItems = totalCounts.totalSentences?.[levelKey] || 0;
  const learnedItems = sentencesStats.byLevel?.[levelKey] || 0;

  if (totalItems === 0) {
    return { icon, title: name, progress: 'N/A', isLocked: true };
  }

  const isCompleted = learnedItems >= totalItems;
  if (isCompleted) {
    return {
      icon,
      title: name,
      progress: `Level Complete!`,
      achievementText: `${totalItems.toLocaleString()} items`,
      isCompleted: true,
      isLocked: false,
    };
  }

  const isLocked = learnedItems === 0;
  return {
    icon,
    title: name,
    progress: `${learnedItems.toLocaleString()}/${totalItems.toLocaleString()}`,
    achievementText: isLocked ? `Learn an N${level} item` : `In Progress`,
    isCompleted: false,
    isLocked: isLocked,
  };
};

/**
 * Finds the user's current level mastery milestone to display.
 * It starts from N5 and works down to N1, showing the first level that isn't complete.
 */
const findActiveLevelMasteryMilestone = (sentencesStats, totalCounts) => {
  const levelsToCheck = [5, 4, 3, 2, 1];

  for (const level of levelsToCheck) {
    const levelKey = String(level);
    const totalItems = totalCounts.totalSentences?.[levelKey] || 0;
    const learnedItems = sentencesStats.byLevel?.[levelKey] || 0;

    // Skip levels with no content
    if (totalItems === 0) continue;

    // Check if this level is complete
    const isCompleted = learnedItems >= totalItems;

    // If this level is NOT complete, it's the active one to show
    if (!isCompleted) {
      return getLevelMasteryState(level, sentencesStats, totalCounts);
    }
  }

  // If all levels are complete, show a "All Levels Complete" milestone
  return {
    icon: '🏆',
    title: 'JLPT Master',
    progress: 'All Levels Complete!',
    achievementText: 'N5 → N1 Complete',
    isCompleted: true,
    isLocked: false,
  };
};

// ===================================================================================
//  Main Milestones Component
// ===================================================================================

const Milestones = ({ sentencesStats, wordsStats, totalCounts, reviewedSentencesData, reviewedWordsData, listeningSeconds }) => {
  const milestoneData = useMemo(() => {
    if (!sentencesStats || !wordsStats || !totalCounts) return [];

    const totalLearnedItems = (sentencesStats.totalLearned || 0) + (wordsStats.totalLearned || 0);
    const hoursListened = Math.floor((listeningSeconds || 0) / 3600);

    const sentencesReviewed = Object.values(reviewedSentencesData || {}).reduce((sum, count) => sum + count, 0);
    const wordsReviewed = Object.values(reviewedWordsData || {}).reduce((sum, count) => sum + count, 0);

    const titaniumItems =
      (sentencesStats.bySrsStage?.['7'] || 0) +
      (sentencesStats.bySrsStage?.['skipped'] || 0) +
      (wordsStats.bySrsStage?.['7'] || 0) +
      (wordsStats.bySrsStage?.['skipped'] || 0);
    const learnedTiers = [1, 10, 100, 500, 1000, 5000, 10000, 15000];
    const reviewTiers = [10, 100, 500, 1000, 5000, 10000, 20000, 30000, 40000, 50000, 100000];
    const titaniumTiers = [1, 5, 10, 25, 50, 100, 500, 1000, 5000, 10000, 15000];
    const hourTiers = [1, 5, 10, 25, 50, 100];

    const itemsReviewed = sentencesReviewed + wordsReviewed;

    const milestones = [
      { ...getMilestoneState(totalLearnedItems, learnedTiers, '🧠', 'Items Learned', 'Learn'), imageKey: 'MILESTONE_3' },
      { ...getMilestoneState(hoursListened, hourTiers, '🎧', 'Hours Listened', 'Listen'), imageKey: 'MILESTONE_1' },
      { ...getMilestoneState(itemsReviewed, reviewTiers, '🔁', 'Items Reviewed', 'Review'), imageKey: 'MILESTONE_2' },
      { ...getMilestoneState(titaniumItems, titaniumTiers, '💎', 'Titanium Items', 'Get'), imageKey: 'MILESTONE_4' },
      // Dynamically find and show the current level mastery milestone
      { ...findActiveLevelMasteryMilestone(sentencesStats, totalCounts), imageKey: 'MILESTONE_5' },
    ];

    return milestones;
  }, [sentencesStats, wordsStats, totalCounts, reviewedSentencesData, reviewedWordsData, listeningSeconds]);

  return (
    <div className="relative overflow-hidden rounded-2xl border border-border bg-background-secondary/70 p-6 shadow-lg backdrop-blur-sm z-[5]">
      {/* Glassmorphic background overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-50" />

      {/* Background decoration */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute right-0 top-0 h-40 w-40 -translate-x-20 -translate-y-20 rounded-full bg-primary" />
        <div className="absolute bottom-0 left-0 h-32 w-32 -translate-x-16 translate-y-16 rounded-full bg-secondary" />
      </div>

      {/* Header */}
      <div className="relative z-10">
        <ChartHeader title="Achievement Milestones" />
      </div>

      {/* Milestones grid */}
      <div className="relative z-10 mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {milestoneData.map((milestoneProps, index) => (
          <div
            key={milestoneProps.title}
            className="h-full animate-fade-in"
            // Stagger the animation with a delay
            style={{ animationDelay: `${index * 100}ms`, opacity: 0 }}
          >
            <MilestoneBadge {...milestoneProps} />
          </div>
        ))}
      </div>
    </div>
  );
};

Milestones.propTypes = {
  sentencesStats: PropTypes.object,
  wordsStats: PropTypes.object,
  totalCounts: PropTypes.object,
  reviewedSentencesData: PropTypes.object,
  reviewedWordsData: PropTypes.object,
};

export default Milestones;
